<template>
  <div>
    <StoreSwitcher v-if="isAdmin" />

    <b-card class="mb-3">
      <b-form @submit.prevent="loadDeposits">
        <b-row>
          <b-col sm="12" md="3">
            <b-form-group label="Data inicial" label-for="deposit-from-date">
              <b-form-input
                id="deposit-from-date"
                v-model="filter.fromDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="3">
            <b-form-group label="Data final" label-for="deposit-to-date">
              <b-form-input
                id="deposit-to-date"
                v-model="filter.toDate"
                type="date"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="3">
            <b-form-group label="Comprovante" label-for="deposit-status">
              <b-form-select
                id="deposit-status"
                v-model="filter.receiptStatus"
                :options="receiptStatusOptions"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="3" class="filter-actions">
            <b-button type="submit" variant="primary">
              <span class="fas fa-filter mr-1" />
              Filtrar
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <b-alert v-if="deposits.error" show variant="danger">
      <span class="fas fa-exclamation-triangle mr-1" />
      {{ deposits.error }}
    </b-alert>

    <b-table
      responsive
      :fields="fields"
      :items="deposits.data"
      :busy="deposits.loading"
      striped
      hover
      show-empty
    >
      <div slot="empty" class="text-center">Nenhum deposito encontrado</div>

      <template #cell(createdAt)="{ value }">
        {{ value | formatDateTime }}
      </template>

      <template #cell(checkout)="{ item }">
        <b-link
          :to="{
            name: 'ReportCheckoutDetails',
            params: { id: item.checkoutId }
          }"
        >
          Caixa #{{ item.checkoutId }}
        </b-link>
        <small
          v-if="item.checkout && item.checkout.store"
          class="d-block text-muted"
        >
          {{ item.checkout.store.name }}
        </small>
      </template>

      <template #cell(createdBy)="{ item }">
        {{ item.createdByUser ? item.createdByUser.name : '-' }}
      </template>

      <template #cell(amount)="{ value }">
        {{ value | formatCurrency }}
      </template>

      <template #cell(receiptStatus)="{ item }">
        <b-badge :variant="item.receiptPhotoUrl ? 'success' : 'warning'">
          {{ item.receiptPhotoUrl ? 'Com comprovante' : 'Pendente' }}
        </b-badge>
        <small
          v-if="item.receiptPhotoUploadedAt"
          class="d-block text-muted mt-1"
        >
          {{ item.receiptPhotoUploadedAt | formatDateTime }}
        </small>
        <small
          v-if="item.receiptPhotoUploadedByUser"
          class="d-block text-muted"
        >
          {{ item.receiptPhotoUploadedByUser.name }}
        </small>
      </template>

      <template #cell(actions)="{ item }">
        <div class="deposit-actions">
          <b-button
            v-if="item.receiptPhotoUrl"
            v-b-tooltip.hover
            size="sm"
            variant="outline-primary"
            title="Ver comprovante"
            @click="openReceipt(item)"
          >
            <span class="fas fa-receipt" />
          </b-button>
          <b-button
            v-b-tooltip.hover
            size="sm"
            :variant="
              item.receiptPhotoUrl ? 'outline-secondary' : 'outline-warning'
            "
            :title="
              item.receiptPhotoUrl
                ? 'Substituir comprovante'
                : 'Anexar comprovante'
            "
            @click="openAttachReceipt(item)"
          >
            <span class="fas fa-paperclip" />
          </b-button>
        </div>
      </template>
    </b-table>

    <b-modal
      v-model="receiptModal.visible"
      title="Recibo do deposito"
      size="lg"
      hide-footer
    >
      <img
        v-if="receiptModal.url"
        :src="receiptModal.url"
        alt="Recibo do deposito"
        class="receipt-image"
      />
    </b-modal>

    <DepositReceiptModal
      v-if="attachReceiptModalOpen"
      v-model="attachReceiptModalOpen"
      @ok="attachReceipt"
      @cancel="closeAttachReceipt"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import { formatCurrency } from '@/helpers/numbers'
import { formatDate, formatDateTime } from '@/helpers/date'
import StoreSwitcher from '@/components/Stores/StoreSwitcher'
import DepositReceiptModal from '@/components/Checkouts/DepositReceiptModal'

export default {
  name: 'BankDepositsReport',
  components: {
    StoreSwitcher,
    DepositReceiptModal
  },
  filters: {
    formatCurrency,
    formatDateTime
  },
  mixins: [
    withAsyncAction({
      key: 'deposits',
      initialState: [],
      fetcher: {
        methodName: 'fetchBankDeposits',
        handler: services.operations.fetchBankDeposits
      }
    })
  ],
  data() {
    return {
      filter: {
        fromDate: formatDate(new Date()),
        toDate: formatDate(new Date()),
        receiptStatus: 'pending'
      },
      receiptStatusOptions: [
        { text: 'Pendentes', value: 'pending' },
        { text: 'Com comprovante', value: 'attached' },
        { text: 'Todos', value: null }
      ],
      receiptModal: {
        visible: false,
        url: null
      },
      attachReceiptModalOpen: false,
      selectedDeposit: null
    }
  },
  computed: {
    ...mapGetters('store', ['currentStoreId', 'isAdmin']),
    fields() {
      return [
        {
          key: 'createdAt',
          label: 'Lancado em'
        },
        {
          key: 'checkout',
          label: 'Caixa'
        },
        {
          key: 'createdBy',
          label: 'Atendente'
        },
        {
          key: 'name',
          label: 'Descricao'
        },
        {
          key: 'amount',
          label: 'Valor'
        },
        {
          key: 'receiptStatus',
          label: 'Comprovante'
        },
        {
          key: 'actions',
          label: 'Acoes'
        }
      ]
    }
  },
  watch: {
    currentStoreId() {
      this.loadDeposits()
    }
  },
  created() {
    this.loadDeposits()
  },
  methods: {
    loadDeposits() {
      this.fetchBankDeposits({
        storeId: this.currentStoreId,
        ...this.filter
      })
    },
    openReceipt(operation) {
      this.receiptModal = {
        visible: true,
        url: operation.receiptPhotoUrl
      }
    },
    openAttachReceipt(operation) {
      this.selectedDeposit = operation
      this.attachReceiptModalOpen = true
    },
    closeAttachReceipt() {
      this.selectedDeposit = null
      this.attachReceiptModalOpen = false
    },
    async attachReceipt({ receiptPhoto }) {
      await services.operations.attachReceipt({
        id: this.selectedDeposit.id,
        receiptPhoto
      })
      this.$root.$toast.success('Comprovante anexado com sucesso')
      this.closeAttachReceipt()
      await this.loadDeposits()
    }
  }
}
</script>

<style scoped>
.filter-actions {
  align-items: flex-end;
  display: flex;
  padding-bottom: 1rem;
}

.deposit-actions {
  display: flex;
  gap: 0.25rem;
}

.receipt-image {
  display: block;
  max-height: 70vh;
  object-fit: contain;
  width: 100%;
}
</style>
